import Splide from '@splidejs/splide';
import { pixelBreakpoints } from '../../helpers/breakpoints';

/**
 * Product-card colour swatches: Splide carousel with responsive overflow behaviour.
 *
 * Uses Splide overflow API — see https://splidejs.com/guides/overflow/
 *
 * @export
 * @class ProductSwatches
 */
export default class ProductSwatches {
  /**
   * @param {HTMLElement} container - Wrapper with `[data-product-item-swatches]`
   */
  constructor(container) {
    const root = container?.querySelector(
      '[data-product-item-swatches-splide]',
    );
    if (!root) {
      return;
    }

    this.root = root;

    this._mqMdUp = window.matchMedia(`(min-width: ${pixelBreakpoints.md})`);

    this._syncOverflowOptions = this._syncOverflowOptions.bind(this);
    this._onBreakpointCross = this._onBreakpointCross.bind(this);

    this.splide = new Splide(root, {
      type: 'slide',
      arrows: false,
      pagination: false,
      autoWidth: true,
      drag: false,
      rewind: false,
    });

    /** Must subscribe before {@link Splide.mount} — see Splide overflow guide */
    this.splide.on('overflow', this._syncOverflowOptions);

    this.splide.mount();

    this._mqMdUp.addEventListener('change', this._onBreakpointCross);

    requestAnimationFrame(() => this._primeOverflowOnce());
  }

  /**
   * @param {boolean} [isOverflow] - Passed from Splide overflow event; omit to read Layout API
   */
  _syncOverflowOptions(isOverflow) {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    const overflow =
      typeof isOverflow === 'boolean'
        ? isOverflow
        : Boolean(this.splide.Components.Layout.isOverflow());

    const mdUp = this._mqMdUp.matches;

    this.splide.options = {
      pagination: false,
      arrows: mdUp && overflow,
      drag: overflow,
    };
  }

  _onBreakpointCross() {
    requestAnimationFrame(() => this._syncOverflowOptions(undefined));
  }

  /** If the initial overflow event raced mount, prime options from Layout */
  _primeOverflowOnce() {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    try {
      this._syncOverflowOptions(this.splide.Components.Layout.isOverflow?.());
    } catch {
      /* noop */
    }
  }

  destroy() {
    this._mqMdUp.removeEventListener('change', this._onBreakpointCross);
    this._mqMdUp = null;

    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }

    this.root = null;
  }
}
